import { c as _c } from "react/compiler-runtime";
import { InstatusStatus, useInstatus } from "@/hooks/useInstatus";
import { getCoreUsageWindow, useCoreUsageTimeseriesNonSuspense, useCurrentCoreUsageNonSuspense } from "@/hooks/useUsage";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { cn } from "@/lib/utils";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { RAIL_WIDTH, RAIL_EXPANDED_WIDTH, RAIL_COLUMN_PADDING } from "./railConstants";
import { memo, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useLiveMetricsSheetState } from "@/hooks/useLiveMetricsSheetState";
import { useTheme } from "next-themes";
import vcpu0Light from "@/assets/vCPU_animations/light/vCPU-0-light.png";
import vcpu0Dark from "@/assets/vCPU_animations/dark/vCPU-0-dark.png";
import vcpu1Light from "@/assets/vCPU_animations/light/vCPU-1-light.png";
import vcpu1Dark from "@/assets/vCPU_animations/dark/vCPU-1-dark.png";
import vcpu2Light from "@/assets/vCPU_animations/light/vCPU-2-light.png";
import vcpu2Dark from "@/assets/vCPU_animations/dark/vCPU-2-dark.png";
import vcpu3Light from "@/assets/vCPU_animations/light/vCPU-3-light.png";
import vcpu3Dark from "@/assets/vCPU_animations/dark/vCPU-3-dark.png";
import vcpu4Light from "@/assets/vCPU_animations/light/vCPU-4-light.png";
import vcpu4Dark from "@/assets/vCPU_animations/dark/vCPU-4-dark.png";
import vcpu5Light from "@/assets/vCPU_animations/light/vCPU-5-light.png";
import vcpu5Dark from "@/assets/vCPU_animations/dark/vCPU-5-dark.png";
type StatusPresentation = {
  label: string;
  dotClassName: string;
};

// getStatusPresentation only ever returns one of a handful of fixed strings
// rendered with the same font/styling, so their measured width is stable for
// the lifetime of the tab. Caching by label text lets every StatusWidgetMorph
// remount (the rail remounts on every client-side navigation) after the first
// reuse a known width instead of re-reading scrollWidth — a forced synchronous
// layout flush that's expensive to pay again right as a new page's content
// (e.g. a large chat transcript) is mounting.
//
// Cached values are the *text-only* width: scrollWidth includes the span's
// current padding (pl-2 expanded, pl-0 collapsed), which is subtracted at
// measure time so collapsed and expanded instances populate the same entry.
const statusLabelWidthCache = new Map<string, number>();

// The label's expanded pl-2, added back onto the cached text-only width where
// it is used: maxWidth sizes the border box (box-sizing: border-box), so the
// padding must be included for the full text to fit.
const STATUS_LABEL_EXPANDED_PADDING_PX = 8;
const getStatusPresentation = (status?: InstatusStatus): StatusPresentation => {
  switch (status) {
    case InstatusStatus.UP:
      return {
        label: "ALL SYSTEMS OPERATIONAL",
        dotClassName: "animate-status-pulse"
      };
    case InstatusStatus.HASISSUES:
      return {
        label: "DEGRADED PERFORMANCE",
        dotClassName: "bg-amber-500"
      };
    case InstatusStatus.UNDERMAINTENANCE:
      return {
        label: "UNDER MAINTENANCE",
        dotClassName: "bg-blue-500"
      };
    default:
      return {
        label: "SYSTEM STATUS UNKNOWN",
        dotClassName: "bg-neutral-500"
      };
  }
};
type VCpuIconProps = {
  totalVCpus: number;
  className?: string;
};
const VCpuIcon = memo(function VCpuIcon(t0) {
  const $ = _c(14);
  const {
    totalVCpus,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [{
      threshold: 5000,
      light: vcpu5Light,
      dark: vcpu5Dark
    }, {
      threshold: 1000,
      light: vcpu4Light,
      dark: vcpu4Dark
    }, {
      threshold: 500,
      light: vcpu3Light,
      dark: vcpu3Dark
    }, {
      threshold: 250,
      light: vcpu2Light,
      dark: vcpu2Dark
    }, {
      threshold: 125,
      light: vcpu1Light,
      dark: vcpu1Dark
    }, {
      threshold: 0,
      light: vcpu0Light,
      dark: vcpu0Dark
    }];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const vcpuImages = t2 as const;
  let t3;
  if ($[1] !== totalVCpus) {
    t3 = vcpuImages.find(t4 => {
      const {
        threshold
      } = t4;
      return totalVCpus >= threshold;
    }) ?? vcpuImages[vcpuImages.length - 1];
    $[1] = totalVCpus;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const selectedImages = t3;
  const t4 = `dark:hidden ${className}`;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      position: "relative",
      zIndex: 50
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== selectedImages.light.src || $[5] !== t4) {
    t6 = <img src={selectedImages.light.src} alt="vCPU" className={t4} style={t5} />;
    $[4] = selectedImages.light.src;
    $[5] = t4;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = `hidden dark:block ${className}`;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      position: "relative",
      zIndex: 50
    };
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== selectedImages.dark.src || $[9] !== t7) {
    t9 = <img src={selectedImages.dark.src} alt="vCPU" className={t7} style={t8} />;
    $[8] = selectedImages.dark.src;
    $[9] = t7;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== t6 || $[12] !== t9) {
    t10 = <>{t6}{t9}</>;
    $[11] = t6;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  return t10;
});

/**
 * Mini pixel bar chart canvas component that displays vCPU usage over time.
 * Uses a rolling window to create a time-series effect where old data shifts left
 * and new data appears on the right.
 *
 * Memoized because the parent `StatusIndicator` lives in `Sidebar`, which
 * re-renders on every route change. Without `memo`, the canvas re-runs its
 * draw effect (including `getComputedStyle` / `getBoundingClientRect`) on
 * every menu click even when `values` hasn't moved, contributing to the
 * choppy click feel.
 */
const MiniPixelBarChartCanvas = memo(function MiniPixelBarChartCanvas(t0) {
  const $ = _c(28);
  const {
    values
  } = t0;
  const {
    resolvedTheme
  } = useTheme();
  const barColor = resolvedTheme === "dark" ? "rgba(240,251,41,0.7)" : "#161718";
  const canvasRef = useRef(null);
  const [cssWidth, setCssWidth] = useState(190);
  const [forceRedraw, setForceRedraw] = useState(0);
  const isVisibleRef = useRef(true);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [rollingValues, setRollingValues] = useState(t1);
  const prevLastValueRef = useRef(null);
  const isInitializedRef = useRef(false);
  const datasetSignatureRef = useRef("");
  let t2;
  let t3;
  if ($[1] !== values) {
    t2 = () => {
      if (values.length === 0) {
        if (datasetSignatureRef.current !== "") {
          isInitializedRef.current = false;
          setRollingValues([]);
          prevLastValueRef.current = null;
          datasetSignatureRef.current = "";
        }
        return;
      }
      const firstValue = values[0];
      const lastValue = values[values.length - 1];
      const signature = `${values.length}-${firstValue}-${lastValue}`;
      if (datasetSignatureRef.current !== "" && datasetSignatureRef.current !== signature) {
        isInitializedRef.current = false;
        setRollingValues([]);
        prevLastValueRef.current = null;
      }
      datasetSignatureRef.current = signature;
    };
    t3 = [values];
    $[1] = values;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] !== cssWidth || $[5] !== values) {
    t4 = () => {
      if (!isInitializedRef.current && values.length > 0) {
        isInitializedRef.current = true;
        const colCount = Math.max(1, Math.floor((cssWidth + 1) / 2));
        const initialValues = values.slice(-colCount);
        setRollingValues(initialValues);
        prevLastValueRef.current = values[values.length - 1];
      } else {
        if (isInitializedRef.current && cssWidth > 0) {
          const colCount_0 = Math.max(1, Math.floor((cssWidth + 1) / 2));
          setRollingValues(prev => {
            if (prev.length > colCount_0) {
              return prev.slice(-colCount_0);
            }
            return prev;
          });
        }
      }
    };
    t5 = [values, cssWidth];
    $[4] = cssWidth;
    $[5] = values;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[8] !== cssWidth || $[9] !== values) {
    t6 = () => {
      if (!isInitializedRef.current || values.length === 0) {
        return;
      }
      const lastValue_0 = values[values.length - 1];
      if (lastValue_0 !== prevLastValueRef.current) {
        prevLastValueRef.current = lastValue_0;
        const colCount_1 = Math.max(1, Math.floor((cssWidth + 1) / 2));
        setRollingValues(prev_0 => {
          const updated = [...prev_0, lastValue_0];
          if (updated.length > colCount_1) {
            return updated.slice(-colCount_1);
          }
          return updated;
        });
      }
    };
    t7 = [values, cssWidth];
    $[8] = cssWidth;
    $[9] = values;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }
      let raf = 0;
      const updateWidth = () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const boundingRect = canvas.getBoundingClientRect();
          const nextWidth = Math.max(1, Math.floor(boundingRect.width));
          setCssWidth(prev_1 => prev_1 === nextWidth ? prev_1 : nextWidth);
        });
      };
      const initialTimeout = setTimeout(() => {
        updateWidth();
      }, 0);
      if (typeof ResizeObserver !== "undefined") {
        const observer = new ResizeObserver(() => {
          updateWidth();
        });
        observer.observe(canvas);
        if (typeof IntersectionObserver !== "undefined") {
          const intersectionObserver = new IntersectionObserver(entries => {
            const isVisible = entries[0]?.isIntersecting;
            if (isVisible) {
              updateWidth();
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  const boundingRect_0 = canvas.getBoundingClientRect();
                  const nextWidth_0 = Math.max(1, Math.floor(boundingRect_0.width));
                  if (nextWidth_0 > 0) {
                    setCssWidth(prev_2 => prev_2 !== nextWidth_0 ? nextWidth_0 : prev_2);
                    setForceRedraw(_temp);
                  }
                });
              });
            }
          }, {
            threshold: 0.01
          });
          intersectionObserver.observe(canvas);
          return () => {
            clearTimeout(initialTimeout);
            cancelAnimationFrame(raf);
            observer.disconnect();
            intersectionObserver.disconnect();
          };
        }
        return () => {
          clearTimeout(initialTimeout);
          cancelAnimationFrame(raf);
          observer.disconnect();
        };
      }
      window.addEventListener("resize", updateWidth);
      return () => {
        clearTimeout(initialTimeout);
        cancelAnimationFrame(raf);
        window.removeEventListener("resize", updateWidth);
      };
    };
    t9 = [];
    $[12] = t8;
    $[13] = t9;
  } else {
    t8 = $[12];
    t9 = $[13];
  }
  useEffect(t8, t9);
  let t10;
  if ($[14] !== barColor || $[15] !== cssWidth || $[16] !== rollingValues || $[17] !== values) {
    t10 = () => {
      const canvas_0 = canvasRef.current;
      if (!canvas_0) {
        return;
      }
      const computedStyle = window.getComputedStyle(canvas_0);
      const isCurrentlyVisible = computedStyle.display !== "none" && computedStyle.visibility !== "hidden" && computedStyle.opacity !== "0";
      const boundingRect_1 = canvas_0.getBoundingClientRect();
      const actualWidth = Math.max(1, Math.floor(boundingRect_1.width));
      if (isCurrentlyVisible && !isVisibleRef.current) {
        isVisibleRef.current = true;
        if (actualWidth > 0 && actualWidth !== cssWidth) {
          setCssWidth(actualWidth);
        }
        if (rollingValues.length === 0 && values.length > 0 && actualWidth > 0) {
          const colCount_2 = Math.max(1, Math.floor((actualWidth + 1) / 2));
          const initialValues_0 = values.slice(-colCount_2);
          setRollingValues(initialValues_0);
          prevLastValueRef.current = values[values.length - 1];
          isInitializedRef.current = true;
        }
        setForceRedraw(_temp2);
        return;
      } else {
        if (!isCurrentlyVisible) {
          isVisibleRef.current = false;
        }
      }
      if (cssWidth <= 0 || actualWidth <= 0) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const boundingRect_2 = canvas_0.getBoundingClientRect();
            const nextWidth_1 = Math.max(1, Math.floor(boundingRect_2.width));
            if (nextWidth_1 > 0 && nextWidth_1 !== cssWidth) {
              setCssWidth(nextWidth_1);
              setForceRedraw(_temp3);
            }
          });
        });
        return;
      }
      if (rollingValues.length === 0 && values.length > 0 && cssWidth > 0) {
        const colCount_3 = Math.max(1, Math.floor((cssWidth + 1) / 2));
        const initialValues_1 = values.slice(-colCount_3);
        setRollingValues(initialValues_1);
        prevLastValueRef.current = values[values.length - 1];
        isInitializedRef.current = true;
        setForceRedraw(_temp4);
        return;
      }
      if (rollingValues.length === 0) {
        return;
      }
      const colCount_4 = Math.max(1, Math.floor((cssWidth + 1) / 2));
      const rowCount = Math.max(1, Math.floor(10));
      const sampled = Array.from({
        length: colCount_4
      }, (_, i) => {
        const offset = colCount_4 - rollingValues.length;
        const idx = i - offset;
        return idx >= 0 ? rollingValues[idx] : 0;
      });
      const max = Math.max(1e-9, ...sampled);
      const columns = sampled.map(v_3 => {
        const normalized = Math.max(0, Math.min(1, v_3 / max));
        if (normalized <= 0) {
          return 0;
        }
        return Math.max(1, Math.round(normalized * rowCount));
      });
      const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
      const canvasWidth = Math.max(1, Math.floor(cssWidth * dpr));
      const canvasHeight = Math.max(1, Math.floor(19 * dpr));
      canvas_0.width = canvasWidth;
      canvas_0.height = canvasHeight;
      const ctx = canvas_0.getContext("2d", {
        alpha: true
      });
      if (!ctx) {
        return;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.imageSmoothingEnabled = false;
      ctx.clearRect(0, 0, cssWidth, 19);
      ctx.fillStyle = barColor;
      for (let col = 0; col < columns.length; col++) {
        const columnHeight = columns[col] ?? 0;
        const x = col * 2;
        if (x >= cssWidth) {
          break;
        }
        if (columnHeight <= 0) {
          ctx.fillRect(x, 18, 1, 1);
        }
        for (let row = 0; row < columnHeight; row++) {
          const y = 18 - row * 2;
          if (y < 0) {
            break;
          }
          ctx.fillRect(x, y, 1, 1);
        }
      }
    };
    $[14] = barColor;
    $[15] = cssWidth;
    $[16] = rollingValues;
    $[17] = values;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== barColor || $[20] !== cssWidth || $[21] !== forceRedraw || $[22] !== rollingValues || $[23] !== values) {
    t11 = [rollingValues, cssWidth, 19, forceRedraw, values, barColor];
    $[19] = barColor;
    $[20] = cssWidth;
    $[21] = forceRedraw;
    $[22] = rollingValues;
    $[23] = values;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  useEffect(t10, t11);
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      width: "100%",
      height: 19
    };
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== cssWidth) {
    t13 = <canvas ref={canvasRef} width={cssWidth} height={19} className="block" style={t12} aria-label="vCPU usage mini chart" role="img" />;
    $[26] = cssWidth;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  return t13;
});

// Rail expand curve: easeOutQuint-ish, matching AppSwitcherRail's WIPE_TRANSITION
// so the morphing status box and the rail panel decelerate together.
const MORPH_EASE = "cubic-bezier(0.22,1,0.36,1)";

// Single fixed chip size shared by the collapsed and expanded layouts. Rendering
// the vCPU chip at one constant size means it never scales or stretches as the
// box width animates — the two crossfading layers only reposition it, they never
// resize it. The chip renders at the PNG's intrinsic 46×66 dimensions in both
// states.
const CHIP_SIZE = "w-[46px] h-[66px] max-w-none";

// Fixed width of the expanded layer's content: the expanded rail minus its px-2
// column padding (8px each side). Pinning the expanded layer to this width means
// its label row and sparkline are laid out once at their final size and only
// opacity-crossfade in/out — they never scale or stretch while the box width
// animates (the canvas sparkline in particular would otherwise be CSS-scaled off
// its last-drawn bitmap, reading as a horizontal stretch). The rail panel's own
// overflow-hidden clips the excess while the box is mid-width.
const EXPANDED_CONTENT_WIDTH = RAIL_EXPANDED_WIDTH - 2 * RAIL_COLUMN_PADDING;

// Padding on all four sides of the standalone status hover card. Symmetric so
// the content insets match top and sides. The chip graphic carries transparent
// top margin, so its upward overflow clips against this edge without cutting any
// visible pixels. Doubles as the -alignOffset that lifts the card so the widget
// inside lands exactly over the collapsed chip in the trigger underneath.
const STATUS_CARD_PADDING = 8;

// Expand/shrink duration for the standalone status hover card. Close to the
// rail's own 240ms so the pop-out feels snappy rather than sluggish; the card
// holds its mount for this long on close so the shrink fully plays before it
// unmounts (see RailStatusHoverCard).
const STATUS_CARD_MORPH_MS = 220;

// On close, the border/shadow/surface layers fade out during only the tail of
// the shrink (via a transition delay) rather than over the whole duration, so
// they stay visibly solid while the box is still shrinking and finish fading
// to nothing exactly as the width lands at 48px — nothing opaque is left to
// linger, and unmounting immediately after doesn't pop the rail's right
// border back in (it's already invisible). See RailStatusHoverCard.
const STATUS_CARD_CLOSE_FADE_MS = 140;
const STATUS_CARD_CLOSE_FADE_DELAY_MS = STATUS_CARD_MORPH_MS - STATUS_CARD_CLOSE_FADE_MS;

// Geometry of the collapsed widget, shared with RailStatusWidgetSkeleton: the
// rail's footer icon rows stack on top of this, so any height drift between
// the live widget and its twin shifts those rows when the rail swaps in.
const STATUS_WIDGET_STACK = "flex w-full flex-col gap-1.5 pb-1";
const STATUS_WIDGET_BOX = "relative h-[62px] w-full rounded-lg border border-[var(--color-border)] bg-transparent";
const STATUS_WIDGET_COLLAPSED_LAYER = "absolute inset-x-0 top-0 flex h-[58px] items-center justify-center";
const STATUS_WIDGET_DOT_ROW = "flex h-6 w-full items-center justify-center rounded-md";

/**
 * Chip graphic with the vCPU value overlaid on its display window: the entire
 * content of the collapsed layout, so nothing has to shrink to fit the narrow
 * box. The chip renders at CHIP_SIZE, the same fixed size the expanded layer
 * uses, so it never appears to scale as the box morphs.
 */
function CollapsedVCpuChip(t0) {
  const $ = _c(10);
  const {
    totalVCpus,
    value
  } = t0;
  const digits = String(value).length;
  const fontSize = digits >= 5 ? "11px" : digits >= 4 ? "13.5px" : digits >= 3 ? "15px" : "18px";
  let t1;
  if ($[0] !== totalVCpus) {
    t1 = <span className="pointer-events-none -translate-y-[11px]"><VCpuIcon totalVCpus={totalVCpus} className={CHIP_SIZE} /></span>;
    $[0] = totalVCpus;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== fontSize) {
    t2 = {
      letterSpacing: "-1.25px",
      fontSize
    };
    $[2] = fontSize;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2 || $[5] !== value) {
    t3 = <span className="type-numerals-pixel-sm pointer-events-none absolute inset-x-0 bottom-[7px] text-center leading-none text-neutral-800 dark:text-neutral-300" style={t2}>{value}</span>;
    $[4] = t2;
    $[5] = value;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t3) {
    t4 = <>{t1}{t3}</>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

/**
 * Static twin of the collapsed widget for the rail's loading skeleton: same
 * box, chip, and status-dot row, at the pending values the live widget itself
 * shows before its queries resolve (a `-` reading, status-unknown dot). The
 * rail skeleton can't render the live widget, whose data hooks would fire org
 * queries while auth is still resolving, but the footer rows above it only land
 * in their final position if this stack is the same height.
 */
export function RailStatusWidgetSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className={STATUS_WIDGET_BOX}><div className={STATUS_WIDGET_COLLAPSED_LAYER}><CollapsedVCpuChip totalVCpus={0} value="-" /></div></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className={STATUS_WIDGET_STACK}>{t0}<div className={STATUS_WIDGET_DOT_ROW}><span className={cn("inline-block size-[8px] rounded-full", getStatusPresentation(undefined).dotClassName)} /></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/**
 * Unified vCPU / system-status widget that morphs between the collapsed (narrow
 * rail) and expanded (200px rail) states instead of hard-swapping two separate
 * components. The box width is *not* animated here: the widget is `w-full`
 * inside the rail's padded column, so it scales in lock-step with the rail's own
 * width transition for free (and the 8px column padding keeps the side margins
 * locked in both states). Only the per-state content crossfades:
 *
 * - The box is a single fixed height in both states; two absolutely-positioned
 *   layers (collapsed = chip over value, expanded = label row + sparkline)
 *   crossfade, so the height never changes as the width animates.
 * - Expanded-only text (`vCPU USAGE`, the status label) is `whitespace-nowrap`
 *   and fades out fast on close (and in slightly late on open) so it is gone
 *   before the shrinking box could ever wrap or clip it.
 * - The value, chip icon, and status dot exist in both states and persist.
 */
export const StatusWidgetMorph = memo(function StatusWidgetMorph(t0) {
  const $ = _c(84);
  const {
    expanded,
    morphMs: t1,
    suppressHoverFill: t2
  } = t0;
  const morphMs = t1 === undefined ? 240 : t1;
  const suppressHoverFill = t2 === undefined ? false : t2;
  const {
    openLiveMetricsSheet
  } = useLiveMetricsSheetState();
  const {
    status
  } = useInstatus();
  const {
    totalVCpus,
    isLoading: isUsageLoading,
    currentUsage
  } = useCurrentCoreUsageNonSuspense();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUserNonSuspense();
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = getCoreUsageWindow(15, 24);
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const {
    startDate,
    endDate
  } = t3;
  const {
    usageSnapshots
  } = useCoreUsageTimeseriesNonSuspense(15, startDate, endDate);
  let t4;
  if ($[1] !== status) {
    t4 = getStatusPresentation(status);
    $[1] = status;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const statusPresentation = t4;
  const statusLabelRef = useRef(null);
  let t5;
  if ($[3] !== statusPresentation.label) {
    t5 = () => statusLabelWidthCache.get(statusPresentation.label) ?? 0;
    $[3] = statusPresentation.label;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const [statusLabelWidth, setStatusLabelWidth] = useState(t5);
  let t6;
  let t7;
  if ($[5] !== statusPresentation.label) {
    t6 = () => {
      const label = statusPresentation.label;
      const cached = statusLabelWidthCache.get(label);
      if (cached != null) {
        setStatusLabelWidth(cached);
        return;
      }
      const measure = () => {
        if (!statusLabelRef.current) {
          return;
        }
        const paddingLeft = parseFloat(getComputedStyle(statusLabelRef.current).paddingLeft) || 0;
        const width = Math.ceil(statusLabelRef.current.scrollWidth - paddingLeft);
        const fontsSettled = !("fonts" in document) || document.fonts.status === "loaded";
        if (width > 0 && fontsSettled) {
          statusLabelWidthCache.set(label, width);
        }
        setStatusLabelWidth(width);
      };
      measure();
      if ("fonts" in document && document.fonts.status !== "loaded") {
        let cancelled = false;
        document.fonts.ready.then(() => {
          if (!cancelled) {
            measure();
          }
        });
        return () => {
          cancelled = true;
        };
      }
    };
    t7 = [statusPresentation.label];
    $[5] = statusPresentation.label;
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  useEffect(t6, t7);
  let t8;
  if ($[8] !== totalVCpus || $[9] !== usageSnapshots) {
    const values = usageSnapshots?.map(_temp5) ?? [];
    t8 = values.length ? [...values] : [totalVCpus];
    $[8] = totalVCpus;
    $[9] = usageSnapshots;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const chartValues = t8;
  let t9;
  bb0: {
    if (!chartValues || chartValues.length === 0) {
      t9 = false;
      break bb0;
    }
    t9 = chartValues.some(_temp6);
  }
  const hasData = t9;
  const valueLabel = isUsageLoading ? "-" : currentUsage ? totalVCpus : "\u2013";
  let t10;
  if ($[11] !== openLiveMetricsSheet) {
    t10 = () => {
      openLiveMetricsSheet();
    };
    $[11] = openLiveMetricsSheet;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const handleVcpuClick = t10;
  const t11 = `opacity ${morphMs}ms ${MORPH_EASE}`;
  let t12;
  if ($[13] !== t11) {
    t12 = {
      transition: t11,
      transitionDelay: "0ms"
    };
    $[13] = t11;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const shownLayerStyle = t12;
  let t13;
  if ($[15] !== morphMs) {
    t13 = Math.round(morphMs * 0.75);
    $[15] = morphMs;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  const t14 = `opacity ${t13}ms ${MORPH_EASE}`;
  let t15;
  if ($[17] !== t14) {
    t15 = {
      transition: t14,
      transitionDelay: "0ms"
    };
    $[17] = t14;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  const hiddenLayerStyle = t15;
  const t16 = suppressHoverFill ? "transition-none hover:bg-transparent dark:hover:bg-transparent" : "transition-colors hover:bg-black/[0.06] dark:hover:bg-white/[0.04]";
  let t17;
  if ($[19] !== t16) {
    t17 = cn(STATUS_WIDGET_BOX, "cursor-pointer", t16);
    $[19] = t16;
    $[20] = t17;
  } else {
    t17 = $[20];
  }
  const t18 = expanded ? hiddenLayerStyle : shownLayerStyle;
  const t19 = expanded ? "pointer-events-none opacity-0" : "opacity-100";
  let t20;
  if ($[21] !== t19) {
    t20 = cn(STATUS_WIDGET_COLLAPSED_LAYER, t19);
    $[21] = t19;
    $[22] = t20;
  } else {
    t20 = $[22];
  }
  let t21;
  if ($[23] !== totalVCpus || $[24] !== valueLabel) {
    t21 = <CollapsedVCpuChip totalVCpus={totalVCpus} value={valueLabel} />;
    $[23] = totalVCpus;
    $[24] = valueLabel;
    $[25] = t21;
  } else {
    t21 = $[25];
  }
  let t22;
  if ($[26] !== t18 || $[27] !== t20 || $[28] !== t21) {
    t22 = <div style={t18} className={t20}>{t21}</div>;
    $[26] = t18;
    $[27] = t20;
    $[28] = t21;
    $[29] = t22;
  } else {
    t22 = $[29];
  }
  const t23 = expanded ? shownLayerStyle : hiddenLayerStyle;
  let t24;
  if ($[30] !== t23) {
    t24 = {
      ...t23,
      width: EXPANDED_CONTENT_WIDTH
    };
    $[30] = t23;
    $[31] = t24;
  } else {
    t24 = $[31];
  }
  const t25 = expanded ? "opacity-100" : "pointer-events-none opacity-0";
  let t26;
  if ($[32] !== t25) {
    t26 = cn("absolute left-0 top-0 flex h-[58px] flex-col justify-center gap-1 px-3", t25);
    $[32] = t25;
    $[33] = t26;
  } else {
    t26 = $[33];
  }
  let t27;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <span className="type-body-xxs-regular whitespace-nowrap text-neutral-800 dark:text-neutral-300">vCPU USAGE</span>;
    $[34] = t27;
  } else {
    t27 = $[34];
  }
  let t28;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = {
      letterSpacing: "-2px"
    };
    $[35] = t28;
  } else {
    t28 = $[35];
  }
  let t29;
  if ($[36] !== valueLabel) {
    t29 = <span className="type-numerals-pixel-sm text-neutral-800 dark:text-neutral-300" style={t28}>{valueLabel}</span>;
    $[36] = valueLabel;
    $[37] = t29;
  } else {
    t29 = $[37];
  }
  let t30;
  if ($[38] !== totalVCpus) {
    t30 = <span className="pointer-events-none relative z-50 -ml-2 overflow-visible"><VCpuIcon totalVCpus={totalVCpus} className={CHIP_SIZE} /></span>;
    $[38] = totalVCpus;
    $[39] = t30;
  } else {
    t30 = $[39];
  }
  let t31;
  if ($[40] !== t29 || $[41] !== t30) {
    t31 = <div className="flex items-center justify-between">{t27}<span className="relative flex h-6 shrink-0 translate-x-[15px] items-center gap-0 overflow-visible">{t29}{t30}</span></div>;
    $[40] = t29;
    $[41] = t30;
    $[42] = t31;
  } else {
    t31 = $[42];
  }
  let t32;
  if ($[43] !== activeGithubOrg || $[44] !== chartValues || $[45] !== hasData) {
    t32 = <div className="min-h-[19px] w-full">{hasData ? <MiniPixelBarChartCanvas key={`${activeGithubOrg}-morph`} values={chartValues} /> : <div className="flex h-[19px] items-center justify-center"><span className="type-body-xxs-regular text-neutral-500 dark:text-neutral-400">No data</span></div>}</div>;
    $[43] = activeGithubOrg;
    $[44] = chartValues;
    $[45] = hasData;
    $[46] = t32;
  } else {
    t32 = $[46];
  }
  let t33;
  if ($[47] !== t24 || $[48] !== t26 || $[49] !== t31 || $[50] !== t32) {
    t33 = <div style={t24} className={t26}>{t31}{t32}</div>;
    $[47] = t24;
    $[48] = t26;
    $[49] = t31;
    $[50] = t32;
    $[51] = t33;
  } else {
    t33 = $[51];
  }
  let t34;
  if ($[52] !== handleVcpuClick || $[53] !== t17 || $[54] !== t22 || $[55] !== t33) {
    t34 = <button type="button" onClick={handleVcpuClick} aria-label="Show vCPU usage details" className={t17}>{t22}{t33}</button>;
    $[52] = handleVcpuClick;
    $[53] = t17;
    $[54] = t22;
    $[55] = t33;
    $[56] = t34;
  } else {
    t34 = $[56];
  }
  const t35 = suppressHoverFill ? "transition-none hover:bg-transparent dark:hover:bg-transparent" : "transition-colors hover:bg-black/[0.06] dark:hover:bg-white/[0.04]";
  let t36;
  if ($[57] !== t35) {
    t36 = cn(STATUS_WIDGET_DOT_ROW, "cursor-pointer", t35);
    $[57] = t35;
    $[58] = t36;
  } else {
    t36 = $[58];
  }
  let t37;
  if ($[59] !== status || $[60] !== statusPresentation.dotClassName) {
    t37 = status === InstatusStatus.UP ? <span className="relative inline-flex transform-gpu items-center justify-center will-change-transform [backface-visibility:hidden]"><span className={cn("absolute size-[12px] rounded-full animate-status-pulse-background", statusPresentation.dotClassName)} style={{
        backgroundColor: "#4CD137"
      }} /><span className="relative inline-block size-[6px] rounded-full animate-status-pulse" style={{
        backgroundColor: "#4CD137"
      }} /></span> : <span className={cn("inline-block size-[8px] transform-gpu rounded-full will-change-transform [backface-visibility:hidden]", statusPresentation.dotClassName)} />;
    $[59] = status;
    $[60] = statusPresentation.dotClassName;
    $[61] = t37;
  } else {
    t37 = $[61];
  }
  const t38 = expanded && statusLabelWidth > 0 ? statusLabelWidth + STATUS_LABEL_EXPANDED_PADDING_PX : 0;
  const t39 = `max-width ${morphMs}ms ${MORPH_EASE}, opacity ${morphMs}ms ${MORPH_EASE}, padding ${morphMs}ms ${MORPH_EASE}`;
  let t40;
  if ($[62] !== t38 || $[63] !== t39) {
    t40 = {
      maxWidth: t38,
      transition: t39,
      transitionDelay: "0ms"
    };
    $[62] = t38;
    $[63] = t39;
    $[64] = t40;
  } else {
    t40 = $[64];
  }
  const t41 = expanded ? "pl-2 opacity-100" : "pointer-events-none pl-0 opacity-0";
  let t42;
  if ($[65] !== t41) {
    t42 = cn("type-body-xxs-regular whitespace-nowrap text-neutral-800 dark:text-neutral-300", t41);
    $[65] = t41;
    $[66] = t42;
  } else {
    t42 = $[66];
  }
  let t43;
  if ($[67] !== statusPresentation.label || $[68] !== t40 || $[69] !== t42) {
    t43 = <span ref={statusLabelRef} style={t40} className={t42}>{statusPresentation.label}</span>;
    $[67] = statusPresentation.label;
    $[68] = t40;
    $[69] = t42;
    $[70] = t43;
  } else {
    t43 = $[70];
  }
  const t44 = expanded ? 14 : 0;
  const t45 = `width ${morphMs}ms ${MORPH_EASE}`;
  let t46;
  if ($[71] !== t44 || $[72] !== t45) {
    t46 = <span aria-hidden={true} className="pointer-events-none shrink-0" style={{
      width: t44,
      transition: t45,
      transitionDelay: "0ms"
    }} />;
    $[71] = t44;
    $[72] = t45;
    $[73] = t46;
  } else {
    t46 = $[73];
  }
  let t47;
  if ($[74] !== t37 || $[75] !== t43 || $[76] !== t46) {
    t47 = <span className="flex items-center">{t37}{t43}{t46}</span>;
    $[74] = t37;
    $[75] = t43;
    $[76] = t46;
    $[77] = t47;
  } else {
    t47 = $[77];
  }
  let t48;
  if ($[78] !== t36 || $[79] !== t47) {
    t48 = <a href="https://status.blacksmith.sh/" target="_blank" rel="noreferrer" aria-label="Status & updates (opens in a new tab)" className={t36}>{t47}</a>;
    $[78] = t36;
    $[79] = t47;
    $[80] = t48;
  } else {
    t48 = $[80];
  }
  let t49;
  if ($[81] !== t34 || $[82] !== t48) {
    t49 = <div className={STATUS_WIDGET_STACK}>{t34}{t48}</div>;
    $[81] = t34;
    $[82] = t48;
    $[83] = t49;
  } else {
    t49 = $[83];
  }
  return t49;
});

/**
 * Interactive hover overlay for the vCPU / system-status widget in the slim
 * app-switcher rail. The collapsed chip stays in the 48px rail; hovering it
 * pops a wider card over the contextual sidebar that morphs the *same* widget
 * from its collapsed to expanded layout using the same curve as the rail's full
 * expansion — so only this portion grows, not the whole rail.
 *
 * The card mounts at the collapsed rail width directly over the trigger, then
 * expands on the next frame so the width transition and the widget's own layer
 * crossfade play in lock-step. On close, `morphed` flips back to collapsed:
 * the width shrinks over the full STATUS_CARD_MORPH_MS, while the border,
 * shadow, and colored surface stay solid through most of that shrink and only
 * fade during its tail (see STATUS_CARD_CLOSE_FADE_MS) — so the box visibly
 * shrinks first, then dissolves right as it reaches the chip, instead of
 * either fading in place before it visibly shrinks or leaving a solid box
 * sitting at the collapsed size after the shrink is done. Both finish at the
 * same instant, so unmounting right after doesn't pop the rail's right border
 * back in (nothing opaque is left to pop). overflow-hidden clips the expanded
 * layer's rightward spill mid-morph, exactly as the rail panel does; the
 * top/bottom padding gives the chip graphic the same room to overflow its box
 * that it has in the rail.
 */
export function RailStatusHoverCard() {
  const $ = _c(33);
  const [open, setOpen] = useState(false);
  const [morphed, setMorphed] = useState(false);
  const closeTimerRef = useRef(null);
  const openRafRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      if (openRafRef.current) {
        cancelAnimationFrame(openRafRef.current);
        openRafRef.current = null;
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const clearPending = t0;
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => clearPending;
    t2 = [clearPending];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = next => {
      clearPending();
      if (next) {
        setOpen(true);
        openRafRef.current = requestAnimationFrame(() => {
          openRafRef.current = requestAnimationFrame(() => setMorphed(true));
        });
      } else {
        setMorphed(false);
        closeTimerRef.current = setTimeout(() => setOpen(false), STATUS_CARD_MORPH_MS + 40);
      }
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleOpenChange = t3;
  const t4 = morphed ? 0 : 1;
  let t5;
  if ($[4] !== t4) {
    t5 = {
      opacity: t4,
      transition: `opacity ${STATUS_CARD_MORPH_MS}ms ${MORPH_EASE}`
    };
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <StatusWidgetMorph expanded={false} suppressHoverFill={true} />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t5) {
    t7 = <HoverCardTrigger asChild={true}><div className="w-full" style={t5}>{t6}</div></HoverCardTrigger>;
    $[7] = t5;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      width: RAIL_EXPANDED_WIDTH
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const t9 = morphed ? RAIL_EXPANDED_WIDTH : RAIL_WIDTH;
  const t10 = morphed ? 1 : 0;
  const t11 = morphed ? `width ${STATUS_CARD_MORPH_MS}ms ${MORPH_EASE}, opacity ${STATUS_CARD_MORPH_MS}ms ${MORPH_EASE}` : `width ${STATUS_CARD_MORPH_MS}ms ${MORPH_EASE}, opacity ${STATUS_CARD_CLOSE_FADE_MS}ms ${MORPH_EASE} ${STATUS_CARD_CLOSE_FADE_DELAY_MS}ms`;
  const t12 = morphed ? "auto" : "none";
  let t13;
  if ($[10] !== t10 || $[11] !== t11 || $[12] !== t12 || $[13] !== t9) {
    t13 = {
      width: t9,
      opacity: t10,
      transition: t11,
      willChange: "width, opacity",
      pointerEvents: t12
    };
    $[10] = t10;
    $[11] = t11;
    $[12] = t12;
    $[13] = t9;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  const t14 = morphed ? 1 : 0;
  const t15 = morphed ? `opacity ${STATUS_CARD_MORPH_MS}ms ${MORPH_EASE}` : `opacity ${STATUS_CARD_CLOSE_FADE_MS}ms ${MORPH_EASE} ${STATUS_CARD_CLOSE_FADE_DELAY_MS}ms`;
  let t16;
  if ($[15] !== t14 || $[16] !== t15) {
    t16 = {
      opacity: t14,
      transition: t15
    };
    $[15] = t14;
    $[16] = t15;
    $[17] = t16;
  } else {
    t16 = $[17];
  }
  let t17;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = cn("pointer-events-none absolute inset-0 rounded-lg", "border border-[var(--color-border)]", "shadow-[8px_0px_20px_0px_rgba(0,0,0,0.08)] dark:shadow-[8px_0px_20px_0px_rgba(0,0,0,0.45)]");
    $[18] = t17;
  } else {
    t17 = $[18];
  }
  let t18;
  if ($[19] !== t16) {
    t18 = <div aria-hidden={true} style={t16} className={t17} />;
    $[19] = t16;
    $[20] = t18;
  } else {
    t18 = $[20];
  }
  let t19;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      padding: STATUS_CARD_PADDING
    };
    $[21] = t19;
  } else {
    t19 = $[21];
  }
  const t20 = !morphed;
  let t21;
  if ($[22] !== morphed || $[23] !== t20) {
    t21 = <div style={t19} className="relative overflow-hidden rounded-lg bg-sidebar"><StatusWidgetMorph expanded={morphed} morphMs={STATUS_CARD_MORPH_MS} suppressHoverFill={t20} /></div>;
    $[22] = morphed;
    $[23] = t20;
    $[24] = t21;
  } else {
    t21 = $[24];
  }
  let t22;
  if ($[25] !== t13 || $[26] !== t18 || $[27] !== t21) {
    t22 = <HoverCardContent side="right" align="start" sideOffset={-(RAIL_WIDTH - RAIL_COLUMN_PADDING)} alignOffset={-STATUS_CARD_PADDING} avoidCollisions={false} style={t8} className="pointer-events-none border-0 bg-transparent p-0 shadow-none dark:bg-transparent"><div style={t13} className="relative">{t18}{t21}</div></HoverCardContent>;
    $[25] = t13;
    $[26] = t18;
    $[27] = t21;
    $[28] = t22;
  } else {
    t22 = $[28];
  }
  let t23;
  if ($[29] !== open || $[30] !== t22 || $[31] !== t7) {
    t23 = <HoverCard open={open} onOpenChange={handleOpenChange} openDelay={30} closeDelay={80}>{t7}{t22}</HoverCard>;
    $[29] = open;
    $[30] = t22;
    $[31] = t7;
    $[32] = t23;
  } else {
    t23 = $[32];
  }
  return t23;
}
function _temp(v) {
  return v + 1;
}
function _temp2(v_0) {
  return v_0 + 1;
}
function _temp3(v_1) {
  return v_1 + 1;
}
function _temp4(v_2) {
  return v_2 + 1;
}
function _temp5(p) {
  return Number.isFinite(p.total?.vcpus) ? p.total.vcpus : 0;
}
function _temp6(v) {
  return v > 0;
}